import {
  Inviter,
  Registerer,
  RegistererState,
  SessionState,
  UserAgent,
} from 'sip.js';

export const BUZZER_BRIDGE_SIP_EVENTS = Object.freeze({
  REGISTRATION_STATUS_CHANGED: 'registration:status-changed',
  CALL_STATUS_CHANGED: 'call:status-changed',
  CALL_INCOMING: 'call:incoming',
  CALL_CONNECTED: 'call:connected',
  CALL_DISCONNECTED: 'call:disconnected',
  REMOTE_AUDIO_BLOCKED: 'remote-audio:blocked',
});

export const BUZZER_BRIDGE_REGISTRATION_STATUS = Object.freeze({
  UNREGISTERED: 'unregistered',
  REGISTERING: 'registering',
  REGISTERED: 'registered',
  FAILED: 'failed',
});

export const BUZZER_BRIDGE_CALL_STATUS = Object.freeze({
  IDLE: 'idle',
  INCOMING: 'incoming',
  ESTABLISHING: 'establishing',
  CONNECTED: 'connected',
});

const DESTINATION_PATTERN = /^\+?[0-9*#]{2,20}$/;
const INBOX_TYPES = new Set(['whatsapp', 'phone', 'twilio', 'vonage']);
const LANGUAGES = new Set(['he', 'en', 'ru']);
const AUDIO_ONLY_OPTIONS = {
  sessionDescriptionHandlerOptions: {
    constraints: { audio: true, video: false },
  },
};

const createRemoteAudioElement = () => {
  const element = document.createElement('audio');
  element.autoplay = true;
  element.playsInline = true;
  element.hidden = true;
  element.dataset.buzzerBridgeRemoteAudio = '';
  document.body.appendChild(element);
  return element;
};

const validateCredentials = credentials => {
  const required = ['extension', 'username', 'password', 'realm', 'wss_url'];
  if (required.some(key => !credentials?.[key])) {
    throw new Error('Incomplete Buzzer Bridge SIP credentials');
  }
};

const validateDestination = destination => {
  if (!DESTINATION_PATTERN.test(destination)) {
    throw new Error('Invalid Buzzer Bridge call destination');
  }
};

const validateCallContext = callContext => {
  if (
    !callContext?.account_id ||
    !callContext?.inbox_id ||
    !INBOX_TYPES.has(callContext?.inbox_type)
  ) {
    throw new Error('Incomplete Buzzer Bridge call context');
  }
  const hasConversation = callContext.conversation_id != null;
  const hasContact = callContext.contact_id != null;
  if (hasConversation === hasContact) {
    throw new Error(
      'Provide exactly one Buzzer Bridge conversation or contact ID'
    );
  }
  if (callContext.lang && !LANGUAGES.has(callContext.lang)) {
    throw new Error('Unsupported Buzzer Bridge call language');
  }
};

class BuzzerBridgeSipClient extends EventTarget {
  constructor({
    sip = { Inviter, Registerer, RegistererState, SessionState, UserAgent },
    audioElementFactory = null,
    mediaStreamFactory = () => new MediaStream(),
  } = {}) {
    super();
    this.sip = sip;
    this.audioElementFactory = audioElementFactory || createRemoteAudioElement;
    this.mediaStreamFactory = mediaStreamFactory;
    this.registrationStatus = BUZZER_BRIDGE_REGISTRATION_STATUS.UNREGISTERED;
    this.callStatus = BUZZER_BRIDGE_CALL_STATUS.IDLE;
    this.userAgent = null;
    this.registerer = null;
    this.registrationPromise = null;
    this.credentials = null;
    this.session = null;
    this.sessionDirection = null;
    this.sessionEstablished = false;
    this.localDisconnectReason = null;
    this.disconnectEmitted = false;
    this.remoteAudioElement = null;
    this.remoteStream = null;
  }

  get isRegistered() {
    return (
      this.registrationStatus === BUZZER_BRIDGE_REGISTRATION_STATUS.REGISTERED
    );
  }

  get hasCall() {
    return this.session !== null;
  }

  register(credentials) {
    if (this.isRegistered) return Promise.resolve();
    if (this.registrationPromise) return this.registrationPromise;

    validateCredentials(credentials);
    this.credentials = { ...credentials };
    const uri = this.sip.UserAgent.makeURI(
      `sip:${credentials.username}@${credentials.realm}`
    );
    if (!uri) throw new Error('Invalid Buzzer Bridge SIP address');

    this.setRegistrationStatus(BUZZER_BRIDGE_REGISTRATION_STATUS.REGISTERING);
    this.userAgent = new this.sip.UserAgent({
      uri,
      authorizationUsername: credentials.username,
      authorizationPassword: credentials.password,
      contactName: credentials.extension,
      contactParams: { transport: 'wss' },
      transportOptions: { server: credentials.wss_url },
      logBuiltinEnabled: false,
      logConfiguration: false,
      sessionDescriptionHandlerFactoryOptions: {
        peerConnectionConfiguration: {
          iceServers: credentials.ice_servers || [],
        },
      },
      delegate: { onInvite: invitation => this.handleInvitation(invitation) },
    });
    this.registerer = new this.sip.Registerer(this.userAgent);
    this.registrationPromise = this.startRegistration();
    return this.registrationPromise;
  }

  async unregister() {
    if (this.session) await this.endCall();
    if (this.registerer && this.isRegistered)
      await this.registerer.unregister();
    if (this.userAgent) await this.userAgent.stop();
    this.registerer = null;
    this.userAgent = null;
    this.credentials = null;
    this.registrationPromise = null;
    this.setRegistrationStatus(BUZZER_BRIDGE_REGISTRATION_STATUS.UNREGISTERED);
  }

  async dial(destination, callContext) {
    if (!this.isRegistered) {
      throw new Error('Buzzer Bridge SIP agent is not registered');
    }
    if (this.session) throw new Error('A Buzzer Bridge call is already active');
    validateDestination(destination);
    validateCallContext(callContext);

    const target = this.sip.UserAgent.makeURI(
      `sip:${destination}@${this.credentials.realm}`
    );
    if (!target) throw new Error('Invalid Buzzer Bridge call destination');

    const inviter = new this.sip.Inviter(this.userAgent, target, {
      ...AUDIO_ONLY_OPTIONS,
      extraHeaders: this.buildInviteHeaders(callContext),
    });
    this.attachSession(inviter, 'outbound');

    try {
      await inviter.invite();
    } catch (error) {
      this.finishCall('failed');
      throw error;
    }
  }

  async acceptIncomingCall() {
    this.ensureIncomingCall();
    await this.session.accept(AUDIO_ONLY_OPTIONS);
  }

  async rejectIncomingCall() {
    this.ensureIncomingCall();
    this.localDisconnectReason = 'canceled';
    await this.session.reject();
  }

  async endCall() {
    if (!this.session) return;

    const { Initial, Establishing, Established } = this.sip.SessionState;
    if (this.session.state === Established) {
      this.localDisconnectReason = 'local';
      await this.session.bye();
    } else if (
      this.sessionDirection === 'outbound' &&
      [Initial, Establishing].includes(this.session.state)
    ) {
      this.localDisconnectReason = 'canceled';
      await this.session.cancel();
    } else if (
      this.sessionDirection === 'inbound' &&
      [Initial, Establishing].includes(this.session.state)
    ) {
      this.localDisconnectReason = 'canceled';
      await this.session.reject();
    }
  }

  setMuted(muted) {
    const peerConnection = this.getPeerConnection();
    if (!peerConnection) return;
    peerConnection.getSenders().forEach(sender => {
      if (sender.track?.kind === 'audio') sender.track.enabled = !muted;
    });
  }

  retryRemoteAudio() {
    return this.remoteAudioElement?.play();
  }

  startRegistration() {
    return new Promise((resolve, reject) => {
      let settled = false;
      const fail = error => {
        if (settled) return;
        settled = true;
        this.registrationPromise = null;
        this.setRegistrationStatus(BUZZER_BRIDGE_REGISTRATION_STATUS.FAILED, {
          error,
        });
        reject(error);
      };

      this.registerer.stateChange.addListener(state => {
        if (state === this.sip.RegistererState.Registered) {
          if (settled) return;
          settled = true;
          this.setRegistrationStatus(
            BUZZER_BRIDGE_REGISTRATION_STATUS.REGISTERED
          );
          resolve();
        } else if (state === this.sip.RegistererState.Terminated) {
          const error = new Error('Buzzer Bridge SIP registration terminated');
          if (settled) {
            this.registrationPromise = null;
            this.setRegistrationStatus(
              BUZZER_BRIDGE_REGISTRATION_STATUS.FAILED,
              { error }
            );
          } else {
            fail(error);
          }
        }
      });

      this.userAgent
        .start()
        .then(() =>
          this.registerer.register({
            requestDelegate: {
              onReject: response => {
                const statusCode = response?.message?.statusCode;
                fail(
                  new Error(
                    `Buzzer Bridge SIP registration rejected${
                      statusCode ? ` (${statusCode})` : ''
                    }`
                  )
                );
              },
            },
          })
        )
        .catch(fail);
    });
  }

  handleInvitation(invitation) {
    if (this.session) {
      invitation.reject({ statusCode: 486 });
      return;
    }
    this.attachSession(invitation, 'inbound');
    this.setCallStatus(BUZZER_BRIDGE_CALL_STATUS.INCOMING, {
      direction: 'inbound',
    });
    this.emit(BUZZER_BRIDGE_SIP_EVENTS.CALL_INCOMING, {
      direction: 'inbound',
    });
  }

  attachSession(session, direction) {
    this.session = session;
    this.sessionDirection = direction;
    this.sessionEstablished = false;
    this.localDisconnectReason = null;
    this.disconnectEmitted = false;
    session.stateChange.addListener(state => this.handleSessionState(state));
    if (direction === 'outbound') {
      this.setCallStatus(BUZZER_BRIDGE_CALL_STATUS.ESTABLISHING, {
        direction,
      });
    }
  }

  handleSessionState(state) {
    if (state === this.sip.SessionState.Established) {
      this.sessionEstablished = true;
      this.attachRemoteMedia();
      this.setCallStatus(BUZZER_BRIDGE_CALL_STATUS.CONNECTED, {
        direction: this.sessionDirection,
      });
      this.emit(BUZZER_BRIDGE_SIP_EVENTS.CALL_CONNECTED, {
        direction: this.sessionDirection,
      });
    } else if (state === this.sip.SessionState.Terminated) {
      const reason =
        this.localDisconnectReason ||
        (this.sessionEstablished ? 'remote' : 'failed');
      this.finishCall(reason);
    }
  }

  finishCall(reason) {
    if (this.disconnectEmitted) return;
    this.disconnectEmitted = true;
    const direction = this.sessionDirection;
    this.cleanupRemoteMedia();
    this.session = null;
    this.sessionDirection = null;
    this.sessionEstablished = false;
    this.localDisconnectReason = null;
    this.setCallStatus(BUZZER_BRIDGE_CALL_STATUS.IDLE, {
      direction,
      reason,
    });
    this.emit(BUZZER_BRIDGE_SIP_EVENTS.CALL_DISCONNECTED, {
      direction,
      reason,
    });
  }

  attachRemoteMedia() {
    const peerConnection = this.getPeerConnection();
    if (!peerConnection) return;
    this.remoteStream = this.mediaStreamFactory();
    peerConnection.getReceivers().forEach(receiver => {
      if (receiver.track) this.remoteStream.addTrack(receiver.track);
    });
    this.remoteAudioElement ||= this.audioElementFactory();
    this.remoteAudioElement.srcObject = this.remoteStream;
    this.remoteAudioElement.play().catch(error => {
      this.emit(BUZZER_BRIDGE_SIP_EVENTS.REMOTE_AUDIO_BLOCKED, { error });
    });
  }

  cleanupRemoteMedia() {
    if (this.remoteAudioElement) this.remoteAudioElement.srcObject = null;
    if (this.remoteStream) {
      this.remoteStream.getTracks().forEach(track => track.stop());
    }
    this.remoteStream = null;
  }

  getPeerConnection() {
    return this.session?.sessionDescriptionHandler?.peerConnection || null;
  }

  buildInviteHeaders(callContext) {
    const headers = [
      `X-MC-Account-Id: ${callContext.account_id}`,
      `X-MC-Inbox-Id: ${callContext.inbox_id}`,
      `X-MC-Inbox-Type: ${callContext.inbox_type}`,
      `X-MC-Agent-Ext: ${this.credentials.extension}`,
    ];
    if (callContext.conversation_id != null) {
      headers.push(`X-MC-Conversation-Id: ${callContext.conversation_id}`);
    } else {
      headers.push(`X-MC-Contact-Id: ${callContext.contact_id}`);
    }
    if (callContext.agent_email) {
      headers.push(`X-MC-Agent-Email: ${callContext.agent_email}`);
    }
    if (callContext.lang) headers.push(`X-MC-Lang: ${callContext.lang}`);
    if (callContext.client_ref) {
      headers.push(`X-MC-Client-Ref: ${callContext.client_ref}`);
    }
    return headers;
  }

  ensureIncomingCall() {
    if (!this.session || this.sessionDirection !== 'inbound') {
      throw new Error('There is no incoming Buzzer Bridge call');
    }
  }

  setRegistrationStatus(status, detail = {}) {
    this.registrationStatus = status;
    this.emit(BUZZER_BRIDGE_SIP_EVENTS.REGISTRATION_STATUS_CHANGED, {
      status,
      ...detail,
    });
  }

  setCallStatus(status, detail = {}) {
    this.callStatus = status;
    this.emit(BUZZER_BRIDGE_SIP_EVENTS.CALL_STATUS_CHANGED, {
      status,
      ...detail,
    });
  }

  emit(type, detail) {
    this.dispatchEvent(new CustomEvent(type, { detail }));
  }
}

export default BuzzerBridgeSipClient;
